import { useEffect, useRef } from 'react'
import { Box } from '@mui/material'
import { useLessonCall } from '@/webrtc/callContext'

/**
 * Видеоплитки участников: своя (без звука, зеркальная) и удалённые.
 *
 * Потоки присваиваются через ref, а не через атрибут src: srcObject нельзя
 * задать разметкой, а пересоздание <video> на каждый рендер обрывало бы
 * картинку.
 */
function VideoTile({ stream, muted, mirrored }: { stream: MediaStream; muted: boolean; mirrored: boolean }) {
  const videoRef = useRef<HTMLVideoElement | null>(null)

  useEffect(() => {
    const element = videoRef.current
    if (!element || element.srcObject === stream) return

    element.srcObject = stream
    void element.play().catch(() => undefined)
  }, [stream])

  return (
    <Box
      component="video"
      ref={videoRef}
      autoPlay
      playsInline
      muted={muted}
      sx={{
        width: '100%',
        maxHeight: 220,
        borderRadius: 1,
        bgcolor: 'common.black',
        objectFit: 'cover',
        transform: mirrored ? 'scaleX(-1)' : 'none',
      }}
    />
  )
}

export function CallVideoGrid() {
  const call = useLessonCall()

  const remoteEntries = Object.entries(call.remoteStreams).filter(
    ([, stream]) => stream.getVideoTracks().length > 0,
  )
  const hasLocalVideo = (call.localStream?.getVideoTracks().length ?? 0) > 0

  if (!hasLocalVideo && remoteEntries.length === 0) return null

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: remoteEntries.length > 1 ? 'repeat(2, 1fr)' : '1fr',
        gap: 1,
        mb: 2,
      }}
    >
      {remoteEntries.map(([userId, stream]) => (
        <VideoTile key={userId} stream={stream} muted mirrored={false} />
      ))}
      {hasLocalVideo && call.localStream && (
        <VideoTile stream={call.localStream} muted mirrored />
      )}
    </Box>
  )
}
